import { List } from 'immutable';

import { getLogger } from '../../utils/core';
import { accountsTransformers } from '../accounts';
import type { Account, AccountProps, AccountSubType, AccountType, UsageType } from '../accounts/accountsTypes';
import { institutionLoginsTypes } from '../institution-logins';
import type { Institution } from '../institutions/institutionsTypes';
import * as types from './accountDiscoveryTypes';
import { replaceRawASCII } from './accountDiscoveryUtils';

const log = getLogger('data/AccountDiscovery/transformers.js');

type AccountsById = {
  get(id: string): Account | undefined;
};

type AccountDiscoveryTransformerMeta = {
  businessId?: string;
};

type QcsAccountAggregatorDetails = {
  type?: string | null;
  subType?: string | null;
  name?: string | null;
  maskedNumber?: string | null;
  balance?: number | null;
  balanceAt?: string | null;
  currency?: string | null;
  aggStatus?: string | null;
  cpAggStatusCode?: string | null;
};

type QcsDiscoveryAggregator = {
  id?: string | null;
  cpId?: string | null;
  cpLoginId?: string | null;
  channel?: string | null;
  cpDetails?: QcsAccountAggregatorDetails | null;
};

type QcsDiscoveryAccount = {
  id?: string | null;
  shadowId?: string | null;
  aggregators?: QcsDiscoveryAggregator[] | null;
};

export type QcsAccountDiscoveryDataResponse = {
  institutionLoginId?: string | null;
  credentialsBlob?: string | null;
  status?: string | null;
  accounts?: QcsDiscoveryAccount[] | null;
};

type AccountDiscoveryStatusAggregatorResponse = {
  isProcessing?: boolean | null;
  aggStatus?: string | null;
  cpAggStatusCode?: string | null;
  cpAggStatusDetail?: string | null;
  userInstructions?: string | null;
};

type AccountDiscoveryStatusResponse = {
  data: {
    institutionLoginId?: string | null;
    isProcessing?: boolean;
    status?: string;
    aggregators?: AccountDiscoveryStatusAggregatorResponse[] | null;
  };
};

type PartnerAuthUrisResponse = {
  data: {
    uris: string[];
  };
};

type AggregationInfoResponse = {
  data: {
    aggregationPlatform?: string;
    pollingReference?: string;
    connectUrl?: string;
    finicity?: {
      connectUrl?: string;
    };
    plaid?: {
      linkToken?: string;
    };
  };
};

// Plaid Link reports a missing metadata value as null.
export type PlaidItemEvent = {
  name?: string; // TODO: does it exists in the API?
  eventName?: string;
  status?: string | null;
  sessionId?: string | null;
  requestId?: string | null;
  institutionId?: string | null;
  institutionName?: string | null;
  errorCode?: string | null;
  errorType?: string | null;
  errorMessage?: string | null;
  exitedAt?: string | null;
};

const toAccountType = (type: string | null | undefined): AccountType | null | undefined =>
  type as AccountType | null | undefined;

const toAccountSubType = (subType: string | null | undefined): AccountSubType | null | undefined =>
  subType as AccountSubType | null | undefined;

const transformAccountsToRequestData = (accounts: Array<Account | AccountProps>) =>
  accounts.reduce((list, account) => {
    list.push(accountsTransformers.transformAccountToRequestData(account));
    return list;
  }, [] as AccountProps[]);

export const transformInstitutionLoginToRequestData = (
  institutionLogin: types.DiscoveryInstitutionLogin,
  accounts: Array<Account | AccountProps> | null = null,
) => ({
  cpSetupMode: institutionLogin.cpSetupMode ? institutionLogin.cpSetupMode : 'DISCOVER_ACCOUNTS_ONLY',
  channel: institutionLogin.channel,
  id: institutionLogin.id !== '0' ? institutionLogin.id : null,
  name: institutionLogin.name,

  cpInstitutionId: institutionLogin.cpInstitutionId,
  institutionId: institutionLogin.institutionId,

  credentials: !institutionLogin.credentials
    ? null
    : institutionLogin.credentials.map((credential) => ({
        key: credential.key,
        value: credential.value,
      })),
  aggregators:
    !institutionLogin.aggregators || institutionLogin.aggregators.size === 0
      ? null
      : [
          {
            cpChannel: institutionLogin.aggregators.get(0)!.channel,
            cpId: institutionLogin.aggregators.get(0)!.cpId,
          },
        ],

  accounts: accounts != null ? transformAccountsToRequestData(accounts) : null,
});

export const mergeDiscoveryAggregatorsWithInstitutionLogin = (
  disoveryAggregators: QcsDiscoveryAggregator[],
  institutionLogin?: types.DiscoveryInstitutionLogin | null,
): types.DiscoveryInstitutionLogin | undefined =>
  institutionLogin?.set(
    'aggregators',
    List(
      disoveryAggregators.map((aggregator) =>
        institutionLoginsTypes.mkInstitutionLoginAggregator({
          channel: aggregator.channel,
          cpId: aggregator.cpId,
        }),
      ),
    ),
  );

export const transformResponseToAccountDiscoveryData = (
  data: QcsAccountDiscoveryDataResponse,
  allAccountsById: AccountsById,
  meta?: AccountDiscoveryTransformerMeta,
): types.AccountDiscoveryData => {
  const activeAccounts: types.ActiveDiscoveredAccount[] = [];
  const deadAccounts: Account[] = [];
  const newAccounts: types.DiscoveredAccount[] = [];
  const unconnectedAccounts: Account[] = [];

  data.accounts?.forEach((account) => {
    let existingAccount = null;
    if (account.id) {
      existingAccount = allAccountsById.get(account.id);
      // todo: should we fail if we cannot find existing account
    }

    // todo: need to handle multiple aggregators of different types, for now
    // todo: will will assume just a single aggregator and fix later
    //
    const aggregator = account.aggregators && account.aggregators.length > 0 ? account.aggregators[0] : null;
    log.debug('Account and Aggregator', account, aggregator, aggregator && aggregator.cpDetails);

    // case a: no id - new discovered account (or one QCS does not know about)
    // case b: id but no aggregator - unconnected account
    // case c: id, cpId, cpDetails - active aggregated account
    // case d: id, cpId, but no cpDetails - dead aggregated account

    let discoveredAccount = null;
    if (aggregator && aggregator.cpDetails) {
      let { type, subType } = aggregator.cpDetails;
      if (type === 'OTHER' || type === 'UNCLASSIFIED' || type === 'UNKNOWN') {
        type = 'UNKNOWN';
        subType = 'UNKNOWN';
      }

      let businessId: string | undefined;
      let usageType: UsageType | undefined;
      if (meta?.businessId) {
        businessId = meta.businessId;
        usageType = 'BUSINESS';
      }

      discoveredAccount = types.mkDiscoveredAccount({
        shadowAccountId: account.shadowId,

        id: aggregator.id,
        cpId: aggregator.cpId,
        cpLoginId: aggregator.cpLoginId,

        channel: aggregator.channel,

        type: toAccountType(type),
        subType: toAccountSubType(subType),

        name: replaceRawASCII(aggregator.cpDetails.name),
        number: aggregator.cpDetails.maskedNumber,
        maskedNumber: aggregator.cpDetails.maskedNumber,

        balance: aggregator.cpDetails.balance,
        balanceAt: aggregator.cpDetails.balanceAt,

        currency: aggregator.cpDetails.currency,

        businessId,
        usageType,
      });
    }
    log.log('Discovered Account is', discoveredAccount);

    if (existingAccount) {
      if (discoveredAccount) {
        activeAccounts.push(
          types.mkActiveDiscoveredAccount({
            account: existingAccount,
            discoveredAccount,
          }),
        );
      } else if (aggregator) {
        deadAccounts.push(existingAccount);
      } else {
        unconnectedAccounts.push(existingAccount);
      }
    } else {
      if (!discoveredAccount) {
        log.error("We didn't discover an existing account yet were also missing new account information", account);
        return;
      }
      const { aggStatus } = aggregator!.cpDetails!;

      if (aggStatus === 'ACCOUNT_NOT_FOUND') {
        log.info("Skipping account because FDS has marked it as not found and we don't know about it anyways", account);
        return;
      }

      const { type } = discoveredAccount;
      if (
        type !== 'BANK' &&
        type !== 'CREDIT' &&
        type !== 'INVESTMENT' &&
        type !== 'LOAN' &&
        type !== 'PAYMENT' &&
        type !== 'UNKNOWN'
      ) {
        log.info(`Skipping unsupported account type, type=${type}`);
        return;
      }

      newAccounts.push(discoveredAccount);
    }
  });

  const accountDiscoveryData = types.mkAccountDiscoveryData({
    institutionLoginId: data.institutionLoginId,
    credentialsBlob: data.credentialsBlob,

    status: data.status,

    activeAccounts: List(activeAccounts),
    deadAccounts: List(deadAccounts),
    newAccounts: List(newAccounts),
    unconnectedAccounts: List(unconnectedAccounts),
  });

  log.debug('AccountDiscoveryData = ', accountDiscoveryData);
  return accountDiscoveryData;
};

export function transformResponseToPartnerAuthUris(response: PartnerAuthUrisResponse): List<string> {
  const { data } = response;
  return List(data.uris);
}

// ================================================================================================
// Transformers for AccountDiscovery Status
// ================================================================================================

export function transformResponseToAccountDiscoveryStatus(
  response: AccountDiscoveryStatusResponse,
): types.AccountDiscoveryStatus {
  const { data } = response;

  return types.mkAccountDiscoveryStatus({
    institutionLoginId: data.institutionLoginId,

    isProcessing: data.isProcessing,
    status: data.status,

    aggregators: !data.aggregators
      ? List()
      : List(
          data.aggregators.map((aggregator) =>
            types.mkAccountDiscoveryAggregatorStatus({
              isProcessing: aggregator.isProcessing,

              aggStatus: aggregator.aggStatus,
              cpAggStatusCode: aggregator.cpAggStatusCode,
              cpAggStatusDetail: aggregator.cpAggStatusDetail,
              userInstructions: aggregator.userInstructions,
            }),
          ),
        ),
  });
}

export const transformPlaidItemActivityToReqestData = (
  event: PlaidItemEvent,
  institution: Institution | null | undefined,
  institutionLogin: types.DiscoveryInstitutionLogin | null | undefined,
) => ({
  ...event,
  fiAggregationPlatform: 'PLAID',
  institutionLoginId: institutionLogin ? institutionLogin.id : null,
  cpInstitutionLoginId: institution?.plaid?.id,
  institutionId: institution?.id,
  institutionName: institution?.name,
});

export const transformResponseToAggregationInfo = (
  response: AggregationInfoResponse,
): types.FinicityAggregationInfo | types.PlaidAggregationInfo | undefined => {
  const { data } = response;
  switch (data.aggregationPlatform) {
    case 'FINICITY':
      return types.mkFinicityAggregationInfo({
        connectUrl: data.finicity!.connectUrl,
        pollingRef: data.pollingReference,
      });
    case 'PLAID':
      return types.mkPlaidAggregationInfo({
        linkToken: data.plaid!.linkToken,
        pollingRef: data.pollingReference,
        connectUrl: data.connectUrl,
      });
    default:
      log.error('Unsupported aggregation platform', data.aggregationPlatform);
      return undefined;
  }
};
